import { Link } from 'react-router-dom';
import { MetricCard } from '@/components/ui/index.js';
import { useFmt } from '@/lib/useFmt.js';
import type { OrdersResponse } from '@/lib/types.js';
import {
  OrderTable,
  SectionEmpty,
  SectionPager,
  SectionTitle,
  SourceBadge,
  planLabel,
  type SectionPaging,
} from './shared.js';

/**
 * 后台赠送 / 闲鱼开户 section. Both flows write to audit_logs with action
 * `admin.user_grant` or `admin.create_xianyu_account`; this view rolls
 * them up by source.
 */
export function GrantsSection({
  data,
  offset,
  onOffsetChange,
  pageSize,
}: { data: OrdersResponse['grants'] } & SectionPaging): JSX.Element {
  const { fmtDateTime } = useFmt();
  const xianyuTotal = data.by_source.xianyu_email + data.by_source.xianyu_username;
  return (
    <section className="mb-8">
      <SectionTitle
        icon="🎁"
        title="后台赠送 / 闲鱼开户"
        emptyHint={data.total === 0 ? '暂无' : undefined}
      />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <MetricCard
          label="累计赠送"
          value={data.total}
          unit="次"
          tone={data.total > 0 ? 'info' : 'neutral'}
        />
        <MetricCard
          label="闲鱼开户"
          value={xianyuTotal}
          unit="次"
          tone="neutral"
          hint={`邮箱 ${data.by_source.xianyu_email} · 账号 ${data.by_source.xianyu_username}`}
        />
        <MetricCard label="月卡赠送" value={data.by_plan.monthly} unit="次" tone="neutral" />
        <MetricCard label="年卡赠送" value={data.by_plan.yearly} unit="次" tone="neutral" />
      </div>

      {data.items.length === 0 ? (
        <SectionEmpty>
          还没有任何后台赠送记录。每次「+1 月」「+1 年」或闲鱼开户都会出现在这里。
        </SectionEmpty>
      ) : (
        <OrderTable>
          <thead className="bg-white/[0.04]">
            <tr>
              <th className="px-4 py-3 text-left text-slate-300 font-medium">收件用户</th>
              <th className="px-4 py-3 text-left text-slate-300 font-medium">来源</th>
              <th className="px-4 py-3 text-left text-slate-300 font-medium">套餐</th>
              <th className="px-4 py-3 text-left text-slate-300 font-medium">到期</th>
              <th className="px-4 py-3 text-left text-slate-300 font-medium">操作人</th>
              <th className="px-4 py-3 text-left text-slate-300 font-medium">时间</th>
            </tr>
          </thead>
          <tbody>
            {data.items.map((g) => (
              <tr key={g.audit_id} className="border-t border-white/[0.08]">
                <td className="px-4 py-3">
                  {g.target_user_id !== null ? (
                    <Link
                      to={`/users/${g.target_user_id}`}
                      className="text-shark-300 hover:text-shark-200"
                    >
                      {g.target_user_email ?? g.target_user_id.slice(0, 8)}
                    </Link>
                  ) : (
                    <span className="text-slate-600">—</span>
                  )}
                </td>
                <td className="px-4 py-3 text-xs">
                  <SourceBadge source={g.source} />
                </td>
                <td className="px-4 py-3 text-slate-300">{planLabel(g.plan)}</td>
                <td className="px-4 py-3 text-slate-400">
                  {g.new_expires_at !== null ? fmtDateTime(g.new_expires_at) : '—'}
                </td>
                <td className="px-4 py-3 text-xs text-slate-400">
                  {g.actor_admin_name ?? (g.actor_admin_id !== null ? g.actor_admin_id.slice(0, 8) : '系统')}
                </td>
                <td className="px-4 py-3 text-slate-400">{fmtDateTime(g.created_at)}</td>
              </tr>
            ))}
          </tbody>
        </OrderTable>
      )}

      <SectionPager total={data.total} paging={{ offset, onOffsetChange, pageSize }} />
    </section>
  );
}
